import LoadingButton from "@mui/lab/LoadingButton/LoadingButton";
import { Alert } from "@mui/material";
import Avatar from "@mui/material/Avatar/Avatar";
import Box from "@mui/material/Box/Box";
import Card from "@mui/material/Card/Card";
import CardContent from "@mui/material/CardContent/CardContent";
import Chip from "@mui/material/Chip/Chip";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import Grid from "@mui/material/Grid/Grid";
import Stack from "@mui/material/Stack/Stack";
import Typography from "@mui/material/Typography/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useTonAddress } from "@tonconnect/ui-react";
import BigNumber from "bignumber.js";
import React, { FC, PropsWithChildren } from "react";
import Countdown from "react-countdown";
import { useParams } from "react-router-dom";
import { Address } from "ton-core";
import { useDaoData } from "../libs/daoService";
import { formatCoinValue } from "../libs/deployerService";
import {
  useExecuteMutation,
  useProposalData,
  useVoteMutation,
} from "../libs/proposalService";
import { DaoContractData } from "../wrappers/Dao";
import { NftProposalData } from "../wrappers/NftProposal";

export const toShortAddress = (address: Address, length = 4): string => {
  const value = address.toString();
  return value.slice(0, length) + "..." + value.slice(-length);
};

export const LoadingProposal: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Card sx={{ width: "100%" }}>
      <CardContent>
        <Typography variant="body2" sx={{ textAlign: "center" }}>
          {children}
        </Typography>
      </CardContent>
    </Card>
  );
};

const getDaoQuorum = (dao: DaoContractData) => {
  return new BigNumber(dao.totalSupply.toString())
    .multipliedBy(dao.quorumNominator)
    .dividedBy(dao.quorumDenominator);
};

export const seeIfCanceled = (
  proposal: NftProposalData,
  dao: DaoContractData | undefined
) => {
  if (proposal.votedFor < proposal.votedAgainst) {
    return true;
  }

  if (dao) {
    if (
      new BigNumber(proposal.votedFor.toString()).isLessThan(getDaoQuorum(dao))
    ) {
      return true;
    }
  }

  return false;
};

const Badge: FC<{
  proposal: NftProposalData;
  dao: DaoContractData | undefined;
}> = ({ proposal, dao }) => {
  const { address } = useParams();
  const client = useQueryClient();

  if (proposal.executed) {
    return (
      <Chip
        label="Executed"
        color="secondary"
        variant="outlined"
        sx={(theme) => ({
          borderColor: theme.palette.secondary.dark,
          color: theme.palette.secondary.dark,
        })}
      />
    );
  }

  if (proposal.expirationDate.getTime() < Date.now()) {
    if (seeIfCanceled(proposal, dao)) {
      return (
        <Chip
          label="Canceled"
          color="secondary"
          variant="outlined"
          sx={(theme) => ({
            borderColor: theme.palette.error.dark,
            color: theme.palette.error.dark,
          })}
        />
      );
    } else {
      return (
        <Chip
          label="Finished"
          color="secondary"
          variant="outlined"
          sx={(theme) => ({
            borderColor: theme.palette.secondary.dark,
            color: theme.palette.secondary.dark,
          })}
        />
      );
    }
  }

  return (
    <Chip
      label={
        <>
          In progress{" "}
          <Countdown
            date={proposal.expirationDate.getTime()}
            onComplete={async () =>
              await client.invalidateQueries([
                address,
                "proposal",
                proposal.votingId.toString(),
              ])
            }
          />
        </>
      }
      sx={(theme) => ({ background: theme.palette.success.light })}
      color="success"
    />
  );
};

const getResult = (proposal: NftProposalData) => {
  const votePositive = new BigNumber(proposal.votedFor.toString());
  const voteNegative = new BigNumber(proposal.votedAgainst.toString());
  const total = votePositive.plus(voteNegative);
  if (total.isEqualTo(0)) {
    return [50, 50] as [number, number];
  }

  const positive = parseFloat(
    votePositive.div(total).multipliedBy(100).toFixed(2)
  );

  const negative = parseFloat((100 - positive).toFixed(2));

  return [positive, negative] as [number, number];
};

const Result: FC<{
  proposal: NftProposalData;
  dao: DaoContractData | undefined;
}> = ({ proposal, dao }) => {
  const [positive, negative] = getResult(proposal);
  return (
    <>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Box
          borderBottom={(theme) => `4px solid ${theme.palette.primary.main}`}
          sx={{
            textAlign: "center",
            p: 1,
            pb: 0.25,
            flexBasis: `${positive}%`,
            whiteSpace: "nowrap",
            borderRight: "none",
          }}
        >
          <Typography variant="body2">Vote For - {positive}%</Typography>
        </Box>
        <Box
          borderBottom={(theme) => `4px solid ${theme.palette.grey[400]}`}
          sx={{
            flexGrow: 1,
            textAlign: "center",
            p: 1,
            pb: 0.25,
            whiteSpace: "nowrap",
            borderLeft: "none",
          }}
        >
          <Typography variant="body2"> Vote Against - {negative}%</Typography>
        </Box>
      </Stack>
      {dao && (
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.25 }}>
          <Typography variant="body2">
            {formatCoinValue(
              proposal.votedFor.toString(),
              dao.daoContent.decimals
            )}
            {!proposal.executed
              ? ` / ${formatCoinValue(
                  getDaoQuorum(dao),
                  dao.daoContent.decimals
                )}`
              : ""}{" "}
            {dao.daoContent.symbol}
          </Typography>
          <Typography variant="body2">
            {formatCoinValue(
              proposal.votedAgainst.toString(),
              dao.daoContent.decimals
            )}{" "}
            {dao.daoContent.symbol}
          </Typography>
        </Stack>
      )}
    </>
  );
};

const ExecuteButton: FC<{
  proposal: NftProposalData;
  dao: DaoContractData | undefined;
}> = React.memo(({ proposal, dao }) => {
  const { mutate, isLoading, error } = useExecuteMutation(proposal);

  if (proposal.executed || seeIfCanceled(proposal, dao)) {
    return <></>;
  }

  return (
    <CardContent>
      <Stack>
        <LoadingButton
          loading={isLoading}
          fullWidth
          color="primary"
          variant="outlined"
          onClick={() => mutate()}
        >
          Execute
        </LoadingButton>
        {error && <Alert>{error.message}</Alert>}
      </Stack>
    </CardContent>
  );
});

const Buttons: FC<{
  proposal: NftProposalData;
  dao: DaoContractData | undefined;
}> = React.memo(({ proposal, dao }) => {
  const { mutate, isLoading, error } = useVoteMutation(proposal);
  const userAddress = useTonAddress(false);

  if (!userAddress) {
    return <></>;
  }
  if (proposal.expirationDate.getTime() < Date.now()) {
    return <ExecuteButton proposal={proposal} dao={dao} />;
  }

  return (
    <CardContent>
      <Stack>
        <Stack direction="row" gap={2}>
          <LoadingButton
            loading={isLoading}
            onClick={() => mutate(true)}
            fullWidth
            color="primary"
            variant="contained"
          >
            For
          </LoadingButton>
          <LoadingButton
            loading={isLoading}
            fullWidth
            color="primary"
            variant="outlined"
            onClick={() => mutate(false)}
          >
            Against
          </LoadingButton>
        </Stack>
        {error && <Alert>{error.message}</Alert>}
      </Stack>
    </CardContent>
  );
});

export const ProposalCard: FC<{ proposalId: bigint }> = React.memo(
  ({ proposalId }) => {
    const { data: proposal } = useProposalData(proposalId);
    const { data: dao } = useDaoData();

    if (!proposal) {
      return (
        <LoadingProposal>
          <CircularProgress />
        </LoadingProposal>
      );
    }

    return (
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            mb={2}
          >
            <Typography variant="body2">
              Owner: {toShortAddress(proposal.ownerAddress)}
            </Typography>
            <Badge proposal={proposal} dao={dao} />
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            gap={2}
            mb={2}
          >
            <Avatar src={proposal.proposalContent.image} />
            <Typography variant="h5" component="h5">
              {proposal.proposalContent.name}
            </Typography>
          </Grid>

          {proposal.proposalContent.description
            .split("\n")
            .map((row, index) => (
              <Typography variant="body1" gutterBottom key={index}>
                {row}
              </Typography>
            ))}

          {/* {proposal.proposal.messagesToSend.map((message) => (
            <Box sx={{ wordWrap: "break-word" }}>{message.toString()}</Box>
          ))} */}

          <Result proposal={proposal} dao={dao} />
        </CardContent>
        <Buttons proposal={proposal} dao={dao} />
      </Card>
    );
  }
);
